import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { currentAnnoScolastico } from "@/lib/annoScolastico";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  CartesianGrid, Legend, LineChart, Line, Cell, LabelList,
} from "recharts";

// Palette settori: A=Asilo/Nido, E=Elementari, F=... M=Medie, N=Nido, S=...
const SETTORE_LABEL = { A: "Asilo/Infanzia", E: "PRIM E", F: "PRIM F", M: "Medie", N: "Nido", S: "Secondaria" };
const SETTORE_COLOR = { A: "#ff9800", E: "#4caf50", F: "#ec407a", M: "#1976d2", N: "#8d6e63", S: "#7e57c2", "—": "#90a4ae" };
const CAUSALE_COLOR = { Iscrizioni: "#0277bd", Rette: "#e65100", Mensa: "#2e7d32" };

// Etichetta % pagato in carattere vistoso (definita fuori dal render per evitare re-mount)
function BigPercentLabel(props) {
  // Recharts passa: { x, y, value, index, ... }
  const { x, value } = props;
  let y = props.y;
  const num = typeof value === "number" ? value : Array.isArray(value) ? Number(value[value.length - 1]) : Number(value);
  if (!isFinite(num)) return null;
  // Se il punto è troppo vicino al top del chart la label finirebbe tagliata:
  // in tal caso la mettiamo SOTTO il dot invece che sopra.
  const displayY = y != null && y < 40 ? y + 34 : (y != null ? y - 16 : 20);
  const text = `${num.toFixed(1)}%`;
  return (
    <text
      x={x}
      y={displayY}
      textAnchor="middle"
      fontFamily="Impact, 'Arial Black', sans-serif"
      fontSize={26}
      fontWeight={900}
      stroke="white"
      strokeWidth={6}
      paintOrder="stroke"
      strokeLinejoin="round"
      fill="#e65100"
    >
      {text}
    </text>
  );
}

const fmtEuro = (v) => (typeof v === "number" ? v : Number(v || 0)).toLocaleString("it-IT", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

// Colore semaforico per % incasso
const pctColor = (v) => {
  const n = Number(v || 0);
  if (n >= 95) return "#1b5e20";
  if (n >= 80) return "#e65100";
  return "#c62828";
};

export default function AnalyticsDashboardPage() {
  const [anno, setAnno] = useState(currentAnnoScolastico());
  const [data, setData] = useState(null);
  const [cr, setCr] = useState(null);  // 🆕 Costi vs Ricavi mensile
  const [top, setTop] = useState(null); // 🆕 Feb 2026: classifiche top alunni/fornitori
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancel = false;
    async function load() {
      setLoading(true); setErr(null);
      try {
        const [r, r2, r3] = await Promise.all([
          api.get("/analytics-dashboard/summary", { params: { anno_scol: anno } }),
          api.get("/analytics-dashboard/costi-ricavi-mensili", { params: { anno_scol: anno } }).catch(() => ({ data: null })),
          api.get("/analytics-dashboard/top-classifiche", { params: { anno_scol: anno, limit: 10 } }).catch(() => ({ data: null })),
        ]);
        if (!cancel) {
          setData(r.data);
          setCr(r2.data);
          setTop(r3.data);
        }
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || e.message);
      } finally {
        if (!cancel) setLoading(false);
      }
    }
    load();
    return () => { cancel = true; };
  }, [anno]);

  const kpi = data?.kpi || {};
  const settoreLabel = (s) => SETTORE_LABEL[s] || s || "—";
  const settoreColor = (s) => SETTORE_COLOR[s] || "#607d8b";

  const anniOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 3, y - 2, y - 1, y, y + 1];
  }, []);

  return (
    <div data-testid="analytics-dashboard-page" style={{ padding: 20, background: "#fafafa", minHeight: "100vh" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: "#1a237e" }}>📊 Analytics Dashboard</h2>
        <label style={{ fontSize: 12, fontWeight: 700, color: "#1a237e" }}>
          A.S.
          <select value={anno} onChange={(e) => setAnno(Number(e.target.value))}
            data-testid="analytics-anno-scol"
            style={{ marginLeft: 6, padding: "4px 10px", fontSize: 12, border: "1px solid #9fa8da" }}>
            {anniOptions.map(y => <option key={y} value={y}>{y}/{String(y + 1).slice(-2)}</option>)}
          </select>
        </label>
        {loading && <span style={{ color: "#1565c0" }}>⏳ Caricamento…</span>}
        {err && <span style={{ color: "#c62828" }}>⚠ {err}</span>}
        <button
          data-testid="analytics-export-pdf-btn"
          onClick={() => {
            const url = `${api.defaults.baseURL}/analytics-dashboard/export-pdf?anno_scol=${anno}`;
            window.open(url, "_blank");
          }}
          disabled={loading}
          title="Genera un PDF stampabile di tutta la dashboard per CdA/archivio"
          style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#1a237e", color: "white",
                   border: "none", borderRadius: 4, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}
        >
          🖨 Esporta PDF
        </button>
        <button
          data-testid="analytics-sync-movimenti-btn"
          onClick={async () => {
            if (!window.confirm(
              `🔄 Sync Movimenti\n\n` +
              `Rilancia la sincronizzazione dei movimenti da Segremat per l'A.S. ${anno}/${(anno % 100) + 1}.\n\n` +
              `La sync fa delete + insert, quindi ripulisce automaticamente eventuali duplicati e riallinea i KPI.\n\n` +
              `Procedere?`
            )) return;
            try {
              const r = await api.post("/sync/movimenti", { anno });
              const d = r.data || {};
              alert(
                `✅ Sync completata\n\n` +
                `📊 Totale movimenti: ${d.totale_movimenti}\n` +
                (d.message ? `ℹ ${d.message}\n` : "") +
                `\nRicarico i KPI...`
              );
              window.location.reload();
            } catch (e) {
              alert("Errore: " + (e.response?.data?.detail || e.message));
            }
          }}
          disabled={loading}
          title="Rilancia la sync movimenti dall'API Segremat — usa questo se noti KPI errati o dati non aggiornati"
          style={{ padding: "6px 14px", fontSize: 12, fontWeight: 700, background: "#2e7d32", color: "white",
                   border: "none", borderRadius: 4, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1 }}
        >
          🔄 Sync Movimenti
        </button>
        {data?.ultima_sync_movimenti && (
          <span
            data-testid="ultima-sync-movimenti"
            title="Ultima sincronizzazione movimenti da Segremat. Se il totale differisce dal gestionale, rilancia la Sync dalla pagina Impostazioni → Sync Segremat."
            style={{ fontSize: 11, color: "#546e7a", background: "#eceff1", padding: "3px 8px", borderRadius: 4, marginLeft: "auto" }}
          >
            🔄 Ultima sync movimenti:&nbsp;
            {new Date(data.ultima_sync_movimenti).toLocaleString("it-IT", {
              day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
            })}
          </span>
        )}
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 12 }}>
        <KpiCard testid="kpi-addebiti" label="📊 Totale Addebiti" value={`€ ${fmtEuro(kpi.totale_addebiti)}`} color="#1a237e" />
        <KpiCard testid="kpi-incassato" label="💶 Incassato" value={`€ ${fmtEuro(kpi.totale_incassato)}`} color="#1b5e20" />
        <KpiCard testid="kpi-da-incassare" label="⏳ Da Incassare" value={`€ ${fmtEuro(kpi.da_incassare)}`} color="#c62828" />
        <KpiCard testid="kpi-alunni" label="🎒 Alunni" value={kpi.n_alunni || 0} sub={`M ${kpi.n_M || 0} · F ${kpi.n_F || 0}`} color="#1565c0" />
      </div>
      {/* KPI % incasso per causale */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <KpiCard testid="kpi-pct-iscrizioni" label="🎟 % Incasso Iscrizioni"
          value={`${(kpi.pct_iscrizioni ?? 0).toFixed(1)}%`}
          sub={`€ ${fmtEuro(kpi.inc_iscrizioni)} / € ${fmtEuro(kpi.add_iscrizioni)}`}
          color={pctColor(kpi.pct_iscrizioni)} />
        <KpiCard testid="kpi-pct-rette" label="📚 % Incasso Rette"
          value={`${(kpi.pct_rette ?? 0).toFixed(1)}%`}
          sub={`€ ${fmtEuro(kpi.inc_rette)} / € ${fmtEuro(kpi.add_rette)}`}
          color={pctColor(kpi.pct_rette)} />
        <KpiCard testid="kpi-pct-mensa" label="🍝 % Incasso Mensa"
          value={`${(kpi.pct_mensa ?? 0).toFixed(1)}%`}
          sub={`€ ${fmtEuro(kpi.inc_mensa)} / € ${fmtEuro(kpi.add_mensa)}`}
          color={pctColor(kpi.pct_mensa)} />
        <KpiCard testid="kpi-pct-altro" label="🔖 % Incasso Altro"
          value={`${(kpi.pct_altro ?? 0).toFixed(1)}%`}
          sub={`€ ${fmtEuro(kpi.inc_altro)} / € ${fmtEuro(kpi.add_altro)}`}
          color={pctColor(kpi.pct_altro)} />
      </div>

      {/* Riga 1: pagamenti per mese */}
      <Card title="📈 Pagamenti per mese" style={{ marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data?.pagamenti_per_mese || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mese" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
            <RTooltip formatter={(v) => `€ ${fmtEuro(v)}`} />
            <Legend />
            <Line type="monotone" dataKey="totale" name="Totale" stroke="#1a237e" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Riga 1b: 🆕 Costi vs Ricavi mensile — 3 grafici distinti (Costi / Ricavi / Netto) */}
      <Card title={`💹 Andamento mensile — Confronto ${cr?.anno_corrente_label || anno + "/…"} vs ${cr?.anno_precedente_label || "anno prec."}`} style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 10, fontSize: 12, color: "#37474f" }}>
          <span><b style={{ color: "#2e7d32" }}>Ricavi AC:</b> € {fmtEuro(cr?.tot_ricavi_ac)}</span>
          <span><b style={{ color: "#c62828" }}>Costi AC:</b> € {fmtEuro(cr?.tot_costi_ac)}</span>
          <span style={{ background: (cr?.tot_netto_ac ?? 0) >= 0 ? "#e8f5e9" : "#ffebee", padding: "2px 8px", borderRadius: 3 }}>
            <b>Netto AC:</b> <span style={{ color: (cr?.tot_netto_ac ?? 0) >= 0 ? "#1b5e20" : "#b71c1c", fontWeight: 800 }}>€ {fmtEuro(cr?.tot_netto_ac)}</span>
          </span>
          <span style={{ marginLeft: 20, color: "#78909c" }}>
            AP → R: € {fmtEuro(cr?.tot_ricavi_ap)} · C: € {fmtEuro(cr?.tot_costi_ap)} · N: € {fmtEuro(cr?.tot_netto_ap)}
          </span>
        </div>

        {/* 3 mini-grafici in griglia responsive */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 14 }}>
          {/* — RICAVI — */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#2e7d32", marginBottom: 4, letterSpacing: 0.3 }}>🟢 RICAVI MENSILI</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={cr?.serie || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mese_label" tick={{ fontSize: 10, fontWeight: 700 }} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <RTooltip formatter={(v) => `€ ${fmtEuro(v)}`} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="ricavi_ac" name={cr?.anno_corrente_label || "AC"} stroke="#2e7d32" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="ricavi_ap" name={cr?.anno_precedente_label || "AP"} stroke="#66bb6a" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* — COSTI — */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#c62828", marginBottom: 4, letterSpacing: 0.3 }}>🔴 COSTI MENSILI</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={cr?.serie || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mese_label" tick={{ fontSize: 10, fontWeight: 700 }} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <RTooltip formatter={(v) => `€ ${fmtEuro(v)}`} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="costi_ac" name={cr?.anno_corrente_label || "AC"} stroke="#c62828" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="costi_ap" name={cr?.anno_precedente_label || "AP"} stroke="#ef9a9a" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* — NETTO — */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#1a237e", marginBottom: 4, letterSpacing: 0.3 }}>🔵 NETTO MENSILE (Ricavi − Costi)</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={cr?.serie || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mese_label" tick={{ fontSize: 10, fontWeight: 700 }} />
                <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => v >= 1000 || v <= -1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <RTooltip formatter={(v) => `€ ${fmtEuro(v)}`} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="netto_ac" name={cr?.anno_corrente_label || "AC"} stroke="#1a237e" strokeWidth={3} dot={{ r: 4, fill: "#1a237e", stroke: "white", strokeWidth: 1.5 }} />
                <Line type="monotone" dataKey="netto_ap" name={cr?.anno_precedente_label || "AP"} stroke="#9fa8da" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Card>

      {/* Riga 2: pagamenti per settore+classe tabellare */}
      <Card title="💰 Pagamenti per settore + classe (ordinati per % incasso)" style={{ marginBottom: 24 }}>
        <div style={{ maxHeight: 400, overflow: "auto" }}>
          <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
            <thead style={{ background: "#eceff1", position: "sticky", top: 0 }}>
              <tr>
                <th style={th}>Settore</th>
                <th style={th}>Classe</th>
                <th style={{ ...th, textAlign: "right" }}>Tot. Addebiti €</th>
                <th style={{ ...th, textAlign: "right" }}>Tot. Incassato €</th>
                <th style={{ ...th, textAlign: "right" }}>Residuo €</th>
                <th style={{ ...th, textAlign: "right" }}>% Incasso</th>
              </tr>
            </thead>
            <tbody>
              {(data?.pagamenti_per_settore_classe || []).slice(0, 60).map((r, i) => {
                const perc = r.percentuale || 0;
                const barColor = perc >= 90 ? "#2e7d32" : perc >= 70 ? "#f57c00" : "#c62828";
                const residuo = r.residuo ?? (r.addebiti - r.incassato);
                const residuoNeg = residuo < -0.01;
                return (
                  <tr key={`${r.settore}-${r.classe}-${i}`} style={{ background: i % 2 ? "#f9f9f9" : "white" }}>
                    <td style={{ ...td, color: settoreColor(r.settore), fontWeight: 700 }}>{settoreLabel(r.settore)}</td>
                    <td style={td}>{r.classe}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace" }}>€ {fmtEuro(r.addebiti)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace", color: "#1b5e20" }}>€ {fmtEuro(r.incassato)}</td>
                    <td
                      title={residuoNeg ? "Rimborsi/note di credito aperti (l'importo pagato supera l'addebito residuo)" : "Da incassare"}
                      style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace",
                               color: residuoNeg ? "#6a1b9a" : residuo > 0.01 ? "#c62828" : "#546e7a",
                               fontWeight: residuoNeg || residuo > 0.01 ? 700 : 400 }}
                    >
                      {residuoNeg ? `↩ € ${fmtEuro(Math.abs(residuo))}` : `€ ${fmtEuro(residuo)}`}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace", color: barColor, fontWeight: 800 }}>
                      {perc.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Riga 3: Incassato vs Addebiti per settore (% pagato) */}
      <Card title="📊 Pagato vs Addebiti per settore (progressione incassi)" style={{ marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={(data?.pagamenti_incasso_vs_addebito || []).filter(r => r.settore !== "S")} margin={{ top: 50, right: 30, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="settore" tick={{ fontSize: 12, fontWeight: 700 }} tickFormatter={settoreLabel} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <RTooltip formatter={(v) => `${Number(v).toFixed(1)}%`} labelFormatter={settoreLabel} />
            <Line type="monotone" dataKey="percentuale_pagato" name="% pagato" stroke="#f57c00" strokeWidth={4} dot={{ r: 8, fill: "#f57c00", stroke: "white", strokeWidth: 2 }}>
              <LabelList
                dataKey="percentuale_pagato"
                content={BigPercentLabel}
              />
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Riga 4: composizione classi (M/F) — ordinata per settore+classe */}
      <Card title="👥 Composizione classi (M/F per classe · ordinata per settore e classe)" style={{ marginBottom: 24 }}>
        {/* 🆕 Totali generali della scuola */}
        <div style={{ display: "flex", gap: 16, marginBottom: 12, padding: "8px 12px", background: "#f5f5f5", borderRadius: 4, fontSize: 12 }}>
          <span><b style={{ color: "#37474f" }}>TOTALE SCUOLA:</b></span>
          <span style={{ color: "#1976d2", fontWeight: 800 }}>♂ Maschi: {kpi.n_M || 0}</span>
          <span style={{ color: "#e91e63", fontWeight: 800 }}>♀ Femmine: {kpi.n_F || 0}</span>
          <span style={{ color: "#37474f", fontWeight: 900 }}>Totale alunni: {kpi.n_alunni || 0}</span>
          <span style={{ color: (kpi.n_M || 0) > (kpi.n_F || 0) ? "#1976d2" : "#e91e63", fontWeight: 900 }}>
            → {(kpi.n_M || 0) > (kpi.n_F || 0) ? `PIÙ MASCHI (+${(kpi.n_M || 0) - (kpi.n_F || 0)})` :
                (kpi.n_M || 0) < (kpi.n_F || 0) ? `PIÙ FEMMINE (+${(kpi.n_F || 0) - (kpi.n_M || 0)})` : "PARI"}
          </span>
        </div>
        {(() => {
          const arr = [...(data?.classi_composizione || [])];
          // Ordina per settore, poi per classe (numero + suffisso lungo prima + alfabetico)
          arr.sort((a, b) => {
            if (a.settore !== b.settore) return String(a.settore).localeCompare(String(b.settore));
            const ma = String(a.classe).match(/^(\d+)(.*)$/);
            const mb = String(b.classe).match(/^(\d+)(.*)$/);
            if (ma && mb) {
              const na = parseInt(ma[1], 10); const nb = parseInt(mb[1], 10);
              if (na !== nb) return na - nb;
              const la = ma[2].length; const lb = mb[2].length;
              if (la !== lb) return lb - la;
              return ma[2].localeCompare(mb[2]);
            }
            return String(a.classe).localeCompare(String(b.classe));
          });
          const rowsWithLabel = arr.map(r => ({
            ...r,
            label: `${settoreLabel(r.settore)} · ${r.classe}`,
            majority: r.M > r.F ? "M" : r.M < r.F ? "F" : "=",
          }));
          return (
            <ResponsiveContainer width="100%" height={420}>
              <BarChart data={rowsWithLabel} margin={{ left: 10, right: 10, bottom: 60, top: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-45} textAnchor="end" height={90} />
                <YAxis tick={{ fontSize: 10 }} />
                <RTooltip
                  formatter={(v, k) => {
                    if (k === "M") return [`${v} maschi`, "Maschi"];
                    if (k === "F") return [`${v} femmine`, "Femmine"];
                    return v;
                  }}
                  labelFormatter={(l, p) => {
                    if (!p || !p[0]) return l;
                    const d = p[0].payload;
                    return `${l} · ${d.majority === "M" ? "PIÙ MASCHI" : d.majority === "F" ? "PIÙ FEMMINE" : "PARI"}`;
                  }}
                />
                <Legend verticalAlign="top" />
                <Bar dataKey="M" name="Maschi" fill="#1976d2" label={{ position: "top", fill: "#1976d2", fontSize: 10, fontWeight: 700 }} />
                <Bar dataKey="F" name="Femmine" fill="#e91e63" label={{ position: "top", fill: "#e91e63", fontSize: 10, fontWeight: 700 }} />
              </BarChart>
            </ResponsiveContainer>
          );
        })()}
      </Card>

      {/* Riga 5: 🆕 Griglia pivot settore × classe (totale alunni) */}
      <Card title="🏫 Griglia Settore × Classe — Totale alunni" style={{ marginBottom: 24 }}>
        {(() => {
          const rows = data?.classi_composizione || [];
          // Ogni colonna è una coppia (settore, classe) — così le classi della PRIM E
          // non si incolonnano con quelle della Medie o Secondaria.
          const setSet = new Set();
          const colSet = new Set();  // "settore|classe"
          const cell = new Map();    // "settore|classe" → totale
          for (const r of rows) {
            setSet.add(r.settore);
            const key = `${r.settore}|${r.classe}`;
            colSet.add(key);
            cell.set(key, (cell.get(key) || 0) + r.totale);
          }
          const settori = [...setSet].sort();
          // Ordina colonne: prima per settore, poi (a parità) numero classe + suffisso lungo prima
          const cols = [...colSet].sort((a, b) => {
            const [sa, ca] = a.split("|"); const [sb, cb] = b.split("|");
            if (sa !== sb) return sa.localeCompare(sb);
            const ma = String(ca).match(/^(\d+)(.*)$/);
            const mb = String(cb).match(/^(\d+)(.*)$/);
            if (ma && mb) {
              const na = parseInt(ma[1], 10); const nb = parseInt(mb[1], 10);
              if (na !== nb) return na - nb;
              const la = ma[2].length; const lb = mb[2].length;
              if (la !== lb) return lb - la;
              return ma[2].localeCompare(mb[2]);
            }
            return String(ca).localeCompare(String(cb));
          });
          // Totali di riga (per settore) — la riga TOT di piede è stata rimossa su richiesta utente
          const rowTot = {};
          for (const s of settori) {
            for (const key of cols) {
              if (key.startsWith(`${s}|`)) {
                rowTot[s] = (rowTot[s] || 0) + (cell.get(key) || 0);
              }
            }
          }
          if (settori.length === 0) return <div style={{ padding: 20, color: "#999", textAlign: "center" }}>Nessun dato per l&apos;anno selezionato</div>;
          return (
            <div style={{ overflow: "auto", maxHeight: 500 }}>
              <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
                <thead style={{ background: "#eceff1", position: "sticky", top: 0 }}>
                  <tr>
                    <th rowSpan={2} style={{ ...th, position: "sticky", left: 0, background: "#eceff1", zIndex: 2, minWidth: 130 }}>Settore ↓ / Classe →</th>
                    {(() => {
                      // header row 1: gruppi settore con colspan
                      const groups = [];
                      let prev = null; let count = 0;
                      for (const k of cols) {
                        const s = k.split("|")[0];
                        if (s === prev) { count++; }
                        else {
                          if (prev !== null) groups.push({ s: prev, n: count });
                          prev = s; count = 1;
                        }
                      }
                      if (prev !== null) groups.push({ s: prev, n: count });
                      return groups.map((g, i) => (
                        <th key={i} colSpan={g.n} style={{ ...th, textAlign: "center", background: settoreColor(g.s), color: "white", minWidth: 52 }}>
                          {settoreLabel(g.s)}
                        </th>
                      ));
                    })()}
                    <th rowSpan={2} style={{ ...th, textAlign: "center", background: "#ffe082", fontWeight: 900, minWidth: 60 }}>TOT</th>
                  </tr>
                  <tr>
                    {cols.map(k => {
                      const c = k.split("|")[1];
                      return <th key={k} style={{ ...th, textAlign: "center", minWidth: 52 }}>{c}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {settori.map((s, ri) => (
                    <tr key={s} style={{ background: ri % 2 ? "#f9f9f9" : "white" }}>
                      <td style={{ ...td, position: "sticky", left: 0, background: ri % 2 ? "#f9f9f9" : "white", fontWeight: 800, color: settoreColor(s), zIndex: 1 }}>
                        {settoreLabel(s)}
                      </td>
                      {cols.map(k => {
                        const [colS] = k.split("|");
                        const v = colS === s ? (cell.get(k) || 0) : 0;
                        return (
                          <td key={k} style={{ ...td, textAlign: "center", fontFamily: "Consolas, monospace",
                            background: v > 0 ? `rgba(26,35,126,${Math.min(0.08 + v / 40, 0.35)})` : undefined,
                            color: v > 0 ? "#1a237e" : "#e0e0e0", fontWeight: v > 0 ? 700 : 400 }}>
                            {v || (colS === s ? "0" : "")}
                          </td>
                        );
                      })}
                      <td style={{ ...td, textAlign: "center", background: "#fff3c4", fontWeight: 900, color: "#e65100" }}>{rowTot[s] || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>

      {/* Riga 6: agevolazioni per settore — Comitato Solidale (a carico) */}
      <Card title="🤝 Agevolazioni accordate per settore (A carico Comitato Solidale)" style={{ marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={(data?.agevolazioni_per_settore || []).filter(r => r.settore !== "A")}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="settore" tick={{ fontSize: 11 }} tickFormatter={settoreLabel} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
            <RTooltip formatter={(v) => `€ ${fmtEuro(v)}`} labelFormatter={settoreLabel} />
            <Legend />
            <Bar dataKey="a_carico_comitato" name="A carico Comitato €" fill="#6a1b9a">
              {(data?.agevolazioni_per_settore || []).filter(r => r.settore !== "A").map((r) => (
                <Cell key={r.settore} fill={SETTORE_COLOR[r.settore] || "#6a1b9a"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Riga 7: Iscrizioni Extra per settore */}
      <Card title="🎨 Iscrizioni Extra per settore" style={{ marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data?.extra_iscritti_per_settore || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="settore" tick={{ fontSize: 11 }} tickFormatter={settoreLabel} />
            <YAxis tick={{ fontSize: 10 }} />
            <RTooltip formatter={(v) => `${v} iscritti`} labelFormatter={settoreLabel} />
            <Legend />
            <Bar dataKey="n" name="Iscritti Extra" fill="#6a1b9a">
              {(data?.extra_iscritti_per_settore || []).map((r) => (
                <Cell key={r.settore} fill={SETTORE_COLOR[r.settore] || "#6a1b9a"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Riga 8: Iscrizioni Extra per corso · giorno · gruppo */}
      <Card title="🎯 Iscrizioni Extra per corso × giorno × gruppo" style={{ marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height={Math.max(280, (data?.extra_iscritti_per_slot?.length || 0) * 26 + 40)}>
          <BarChart data={data?.extra_iscritti_per_slot || []} layout="vertical" margin={{ left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
            <YAxis dataKey="label" type="category" tick={{ fontSize: 11 }} width={250} interval={0} />
            <RTooltip formatter={(v) => `${v} iscritti`} />
            <Bar dataKey="n" name="Iscritti" fill="#1976d2">
              <LabelList dataKey="n" position="right" formatter={(v) => v > 0 ? v : ""}
                style={{ fill: "#0d47a1", fontWeight: 700, fontSize: 11 }} />
              {(data?.extra_iscritti_per_slot || []).map((r, i) => {
                // Colore per giorno (LUN=blu, MAR=verde, MER=arancio, GIO=rosso, VEN=viola)
                const cmap = { LUN: "#1976d2", MAR: "#388e3c", MER: "#f57c00", GIO: "#c62828", VEN: "#6a1b9a", SAB: "#0097a7", DOM: "#546e7a" };
                return <Cell key={i} fill={cmap[r.giorno_label] || "#1976d2"} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* 🆕 Feb 2026: Classifiche Top 10 alunni + fornitori */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Card title={`🏆 Top 10 Alunni per Spesa · A.S. ${anno}/${(anno % 100) + 1}`}>
          {(top?.top_alunni?.length || 0) === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Nessun dato.</div>
          ) : (
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="dash-top-alunni">
              <thead>
                <tr>
                  <th style={{ ...th, width: 30, textAlign: "center" }}>#</th>
                  <th style={th}>ALUNNO</th>
                  <th style={{ ...th, width: 50, textAlign: "center" }}>SET.</th>
                  <th style={{ ...th, width: 60, textAlign: "center" }}>CLASSE</th>
                  <th style={{ ...th, width: 50, textAlign: "right" }}>MOV.</th>
                  <th style={{ ...th, width: 100, textAlign: "right" }}>SPESA €</th>
                </tr>
              </thead>
              <tbody>
                {top.top_alunni.map((r, i) => (
                  <tr key={r.cod} data-testid={`dash-top-alunno-${i}`}>
                    <td style={{ ...td, textAlign: "center", fontWeight: 800, color: i < 3 ? "#e65100" : "#78909c", fontFamily: "monospace" }}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {r.cognome} {r.nome}
                      <span style={{ marginLeft: 6, fontSize: 10, color: "#90a4ae", fontFamily: "monospace" }}>#{r.cod}</span>
                    </td>
                    <td style={{ ...td, textAlign: "center", fontWeight: 700, color: settoreColor(r.settore) }}>{r.settore}</td>
                    <td style={{ ...td, textAlign: "center", fontFamily: "monospace" }}>{r.classe}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "monospace", color: "#546e7a" }}>{r.n_movimenti}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 800, color: "#0d47a1" }}>€ {fmtEuro(r.totale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title={`🏢 Top 10 Fornitori per Fatturato · A.S. ${anno}/${(anno % 100) + 1}`}>
          {(top?.top_fornitori?.length || 0) === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#888" }}>Nessun dato.</div>
          ) : (
            <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }} data-testid="dash-top-fornitori">
              <thead>
                <tr>
                  <th style={{ ...th, width: 30, textAlign: "center" }}>#</th>
                  <th style={th}>RAGIONE SOCIALE</th>
                  <th style={{ ...th, width: 90 }}>CITTÀ</th>
                  <th style={{ ...th, width: 50, textAlign: "right" }}>FATT.</th>
                  <th style={{ ...th, width: 110, textAlign: "right" }}>TOTALE €</th>
                </tr>
              </thead>
              <tbody>
                {top.top_fornitori.map((r, i) => (
                  <tr key={r.primary_key || i} data-testid={`dash-top-fornitore-${i}`}>
                    <td style={{ ...td, textAlign: "center", fontWeight: 800, color: i < 3 ? "#e65100" : "#78909c", fontFamily: "monospace" }}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 600, textTransform: "uppercase" }}>
                      {r.ragione_sociale}
                      {r.piva && <span style={{ marginLeft: 6, fontSize: 10, color: "#90a4ae", fontFamily: "monospace" }}>{r.piva}</span>}
                    </td>
                    <td style={{ ...td, fontSize: 11 }}>{r.citta || "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "monospace", color: "#546e7a" }}>{r.n_fatture}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: "Consolas, monospace", fontWeight: 800, color: "#0d47a1" }}>€ {fmtEuro(r.totale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color, testid }) {
  return (
    <div data-testid={testid} style={{
      background: "white", padding: "12px 14px", borderRadius: 6,
      borderLeft: `5px solid ${color}`, boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    }}>
      <div style={{ fontSize: 11, color: "#546e7a", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: "#78909c", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Card({ title, children, style }) {
  return (
    <div style={{
      background: "white", padding: 14, borderRadius: 6,
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)", ...style,
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#37474f", marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  );
}

const th = { padding: "6px 10px", textAlign: "left", fontSize: 11, fontWeight: 800, color: "#37474f", borderBottom: "2px solid #b0bec5" };
const td = { padding: "5px 10px", borderBottom: "1px solid #eee" };
